import * as Sentry from '@sentry/vue'

// Shared Sentry reporting for the Marketing Agent. Send IDs, statuses and codes
// only — never post text, edited text or topic names.
type ReportContext = {
  planId?: string | null
  slotId?: string | null
  attempt?: number
  code?: string | null
  reason?: string | null
  [key: string]: unknown
}

function tagsFor(operation: string, context: ReportContext) {
  return {
    feature: 'marketing-agent',
    operation,
    ...(context.attempt ? { attempt: String(context.attempt) } : {}),
    ...(context.code ? { code: context.code } : {}),
  }
}

/** A thrown error: network, GraphQL or unexpected. */
export function reportError(operation: string, error: unknown, context: ReportContext = {}) {
  Sentry.captureException(error, {
    tags: tagsFor(operation, context),
    extra: context,
  })
}

/**
 * A failure that is not an exception on the frontend: a SKIPPED slot result, a
 * FAILED slot, a schedule error, a timeout or a retry limit that was reached.
 * Grouped by operation and code, not by the message text.
 */
export function reportOutcome(
  operation: string,
  context: ReportContext & { level?: Sentry.SeverityLevel } = {}
) {
  const { level = 'error', ...extra } = context
  const code = context.code || 'unknown'
  Sentry.captureMessage(`marketing-agent:${operation}:${code}`, {
    level,
    tags: tagsFor(operation, context),
    extra,
    fingerprint: ['marketing-agent', operation, code],
  })
}

// Polling sees the same failed slot every few seconds; report it once per session.
const reportedOnce = new Set<string>()

export function reportOutcomeOnce(
  key: string,
  operation: string,
  context: Parameters<typeof reportOutcome>[1] = {}
) {
  if (reportedOnce.has(key)) return
  reportedOnce.add(key)
  reportOutcome(operation, context)
}
